import React, { RefObject, useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector, useTranslate } from '../../../hooks/common';
import { Curve } from '../../../models';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, ChartOptions, ChartData, ChartDataset } from 'chart.js';
import { CurveType, DiagramOptionsProperties, ScalingType } from '../../../enums';
import { TRANSLATIONS } from '../../../constants';
import { deepCopy } from '../../../services';
import Select from '../../common/Select';
import { setDisabledCurveTypes } from '../../../store';
import Legend from './Legend';
import MultilineTooltip from '../../common/MultilineTooltip';
import { InnoButton } from '@innomotics/innomotics-build-react-lib';

interface DiagramProps {
    curves: Curve[];
    lineRef: RefObject<ChartJS<'line'>>;
    diagramOptions?: ChartOptions<'line'>;
    optionProperties: DiagramOptionsProperties;
    onScalingChange: (value: { [key in ScalingType]?: string }) => void;
    onZoomChange: (property: DiagramOptionsProperties, ref: RefObject<ChartJS<'line'>>) => void;
    onZoomReset: (property: DiagramOptionsProperties, ref: RefObject<ChartJS<'line'>>, curves: Curve[]) => void;
}

const Diagram = ({ lineRef, optionProperties, curves, diagramOptions, onZoomChange, onScalingChange, onZoomReset }: DiagramProps) => {
    const translate = useTranslate();
    const dispatch = useAppDispatch();
    const isAbsoluteScaling = useAppSelector(state => state.diagram.isAbsoluteScaling);
    const calculationSuccessful = useAppSelector(state => state.motor.calculationSuccessful);
    const disabledCurveTypes = useAppSelector(state => state.diagram.disabledCurveTypes);
    const visibleCurves = curves && disabledCurveTypes && curves.filter(x => disabledCurveTypes.every(y => y !== x.curveType)) as Curve[];
    const chartData: ChartData<'line'> = { datasets: visibleCurves as ChartDataset<'line'>[] };
    const disabled = !calculationSuccessful;
    const [redrawChart, setRedrawChart] = useState(false);

    useEffect(() => {
        setRedrawChart(diagramOptions?.plugins?.zoom?.pan?.enabled ?? false);
    }, [diagramOptions?.plugins?.zoom?.pan?.enabled]);

    const changeCurvesOrder = (chartData: ChartData) => {
        const newChartData = deepCopy(chartData);

        if (newChartData && newChartData.datasets[0] && newChartData.datasets[0].order && optionProperties === 'torqueOverSpeedDiagramOptions') {
            newChartData.datasets[0].order = 2;
        }

        return newChartData as ChartData<'line'>;
    };

    const handleCurveLabelClick = (curveType: CurveType) => {
        dispatch(setDisabledCurveTypes(disabledCurveTypes.some(x => x === curveType)
            ? disabledCurveTypes.filter(x => x !== curveType)
            : [...disabledCurveTypes, curveType]));

        setRedrawChart(diagramOptions?.plugins?.zoom?.pan?.enabled ?? false);
    };

    const renderChartInputs = () => {
        const scalingSelectValues: { [key in ScalingType]?: string }[] = [
            { '0': translate(TRANSLATIONS.diagram.scaling.absoluteScaling) },
            { '1': translate(TRANSLATIONS.diagram.scaling.relatedScaling) }
        ];
        const scalingSelectValue = isAbsoluteScaling ? scalingSelectValues[ScalingType.Absolute] : scalingSelectValues[ScalingType.Related];
        const zoomEnabled = diagramOptions?.plugins?.zoom?.zoom?.drag?.enabled;

        return (
            <div className='diagram-inputs'>
                <Select
                    dark
                    disabled={disabled}
                    label={translate(TRANSLATIONS.diagram.scaling.title)}
                    value={scalingSelectValue}
                    values={scalingSelectValues}
                    mapToString={x => Object.values(x).toString()}
                    onSelect={onScalingChange}
                />
                <div className='diagram-actions'>
                    <InnoButton colorVariant='light' variant='secondary' disabled={disabled} onClick={() => onZoomChange(optionProperties, lineRef)}>
                        {translate((TRANSLATIONS.diagram as never)[zoomEnabled ? 'enableDrag' : 'enableZoom'])}
                    </InnoButton>
                    <InnoButton colorVariant='light' variant='secondary' disabled={disabled} onClick={() => onZoomReset(optionProperties, lineRef, curves)}>
                        {translate(TRANSLATIONS.diagram.resetZoom)}
                    </InnoButton>
                    <MultilineTooltip idName='diagramInfo' text={translate(TRANSLATIONS.tooltips.diagram)} html>
                        <></>
                    </MultilineTooltip>
                </div>
            </div>
        );
    };

    return (
        <div className='graph-container'>
            {Boolean(curves.length) && <>
                <Line ref={lineRef} data={changeCurvesOrder(chartData)} options={diagramOptions} redraw={redrawChart} />
                {renderChartInputs()}
                <Legend curves={curves} visibleCurves={visibleCurves} onCurveLabelClick={handleCurveLabelClick} />
            </>}
        </div>
    );
};

export default Diagram;
